import { useState, useEffect, useMemo } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Bar } from 'recharts';
import { ExtendedStage } from './valueStreamData';

// Add this interface at the beginning of the file, before the components
// Add window interface extension for TypeScript
declare global {
  interface Window {
    valueStreamChart?: {
      setSelectedPoint: (point: any) => void;
    };
  }
}

interface ValueStreamPerformanceChartProps {
  stages: ExtendedStage[];
  globalMetrics: {
    numberOfOpportunities?: number;
    averageSalesValue?: number;
    processMeanTime?: number;
  };
}

interface VisibleLines {
  currentValue: boolean;
  baselineValue: boolean;
  healthPercentage: boolean;
}

/**
 * Determines the color for a percentage value based on thresholds
 * @param percentage - The percentage value as a number
 * @returns The appropriate color category: 'GREEN', 'YELLOW', or 'RED'
 */
const getPercentageColor = (percentage: number): 'GREEN' | 'YELLOW' | 'RED' => {
  if (percentage >= 90) {
    return 'GREEN';
  } else if (percentage >= 75 && percentage <= 89) {
    return 'YELLOW';
  } else {
    return 'RED';
  }
};

// Add formatCurrency function
const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}k`;
  }
  return `$${value}`;
};

export function ValueStreamPerformanceChart({ stages }: ValueStreamPerformanceChartProps) {
  // State to track which lines are visible
  const [visibleLines, setVisibleLines] = useState<VisibleLines>({
    currentValue: true,
    baselineValue: true,
    healthPercentage: true
  });

  // Handle legend item click to toggle visibility
  const handleLegendClick = (dataKey: string) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey as keyof VisibleLines]
    }));
  };

  // Process stages data for the chart
  const chartData = useMemo(() => stages.map((stage) => {
    if (!stage.metrics) return null;

    const {
      baselineValue = 0,
      currentValue = 0,
      healthPercentage = 0,
    } = stage.metrics;

    return {
      name: stage.name,
      symbol: stage.symbol,
      baselineValue,
      currentValue,
      healthPercentage,
    };
  }).filter(Boolean), [stages]);

  // Calculate dynamic domain for Y axis based on visible lines, memoized
  const calculateYDomain = () => {
    // Find maximum value from visible lines
    const maxValue = Math.max(
      ...chartData.filter((d): d is NonNullable<typeof d> => d !== null)
        .flatMap(d => {
          const values: number[] = [];
          if (visibleLines.currentValue && typeof d.currentValue === 'number') {
            values.push(d.currentValue);
          }
          if (visibleLines.baselineValue && typeof d.baselineValue === 'number') {
            values.push(d.baselineValue);
          }
          return values;
        })
    );

    // If no lines are visible or all values are 0
    if (maxValue <= 0) {
      return [0, 1000000]; // Default to $1M for empty chart
    }

    // For values around $1M, use $1.2M as max
    if (maxValue >= 800000 && maxValue <= 1000000) {
      return [0, 1200000];
    }

    // For values between $1M and $2M
    if (maxValue > 1000000 && maxValue <= 2000000) {
      // Round up to nearest 250k
      const roundedMax = Math.ceil(maxValue / 250000) * 250000;
      return [0, roundedMax];
    }

    // For values between $500k and $1M
    if (maxValue > 500000 && maxValue < 1000000) {
      return [0, 1000000]; // Always show up to $1M
    }

    // For other ranges, round to a nice number
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    let niceMax = Math.ceil(maxValue / magnitude) * magnitude;

    // Add 10% headroom
    niceMax = Math.ceil(niceMax * 1.1);

    return [0, niceMax];
  };

  // Calculate appropriate ticks for this domain, memoized
  const yTicks = useMemo(() => {
    return generateYAxisTicks(calculateYDomain()[1]);
  }, [calculateYDomain]); // Recalculate when calculateYDomain changes

  useEffect(() => {
    // This effect now just confirms the values used in the render
  }, [calculateYDomain, yTicks]);

  // Custom tooltip component with stage position removed
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <div className="bg-white border rounded-lg shadow-lg max-w-2xl">
          <div className="flex items-center justify-between border-b pb-2 m-2">
            <div>
              <h3 className="font-medium text-zinc-800">{data.name} Stage</h3>
            </div>
          </div>
          <div className="space-y-3 m-2">
            <div className="grid grid-cols-1 gap-3">
              <div className="bg-white p-3 rounded-md border shadow-sm">
                <div className="text-xs font-medium text-zinc-700 mb-1">Baseline Value (VB)</div>
                <div className="text-lg font-semibold text-green-600">
                  {formatCurrency(data.baselineValue)}
                </div>
                <div className="h-4"></div>
              </div>

              <div className="bg-white p-3 rounded-md border shadow-sm">
                <div className="text-xs font-medium text-zinc-700 mb-1">Current Value (CV)</div>
                <div className="text-lg font-semibold text-blue-600">
                  {formatCurrency(data.currentValue)}
                </div>
                <div className="h-4"></div>
              </div>

              <div className="bg-white p-3 rounded-md border shadow-sm">
                <div className="text-xs font-medium text-zinc-700 mb-1">Health Percentage</div>
                <div className="text-lg font-semibold text-yellow-600">
                  {Math.round(data.healthPercentage)}%
                </div>
                <div className="h-4"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom legend that toggles visibility when clicked
  const renderLegend = (props: any) => {
    const { payload } = props;

    return (
      <div className="flex flex-wrap justify-end items-center mb-2">
        {payload.map((entry: any, index: number) => {
          // Extract the dataKey from the entry
          const dataKey = entry.dataKey;
          const isVisible = visibleLines[dataKey as keyof VisibleLines];

          return (
            <div
              key={`legend-${index}`}
              className={`flex items-center cursor-pointer px-2 rounded ml-4 
                ${isVisible ? 'opacity-100' : 'opacity-50'} transition-opacity`}
              onClick={() => handleLegendClick(dataKey)}
            >
              <div
                className="w-3 h-3 mr-2 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-xs font-medium">{entry.value}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // Custom X-axis tick component to display stage names with percentage values
  const CustomXAxisTick = (props: any) => {
    const { x, y, payload, visibleTicksCount, width } = props;
    const totalStages = Array.isArray(chartData) ? chartData.length : 0;
    const countForWidth = totalStages > 0 ? totalStages : (visibleTicksCount || 1);
    const stage = chartData[payload.index];
    const healthPercentage = stage?.healthPercentage || 0; // default fallback
    const formattedPercentage = `${Math.round(healthPercentage)}%`;

    // Get color based on percentage
    const percentageColor = getPercentageColor(healthPercentage);
    
    // Apply color styling based on percentage
    let bgColor = '#edebe9'; // Default light gray background
    let borderColor = '#D2D0CE'; // Default border color
    
    if (percentageColor === 'RED') {
      bgColor = '#FDE8E8'; // Light red background for low percentages
      //borderColor = '#FDE8E8'; // Red border for low percentages
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <foreignObject width={width / countForWidth} height={70} x={-payload.offset} y={0}>
          <div
            className="h-full flex flex-col items-center justify-center text-center"
            style={{ fontFamily: 'Segoe UI', backgroundColor: bgColor, border: `1px solid ${borderColor}` }}
          >
            <div className="text-sm">{payload.value}</div>
            <div className="font-bold">{formattedPercentage}</div>
          </div>
        </foreignObject>
      </g>
    );
  };

  return (
    <div className="w-full space-y-3">
      {/* Legend */}
      <div className="w-full">
        {renderLegend({
          payload: [
            { dataKey: 'currentValue', value: 'Current Value (CV)', color: '#2563eb' },
            { dataKey: 'baselineValue', value: 'Baseline Value (VB)', color: '#16a34a' },
            { dataKey: 'healthPercentage', value: 'Health Percentage', color: '#FCD842' }
          ]
        })}
      </div>
      {/* Chart */}
      <div className="h-[20vh]">
        <ResponsiveContainer>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3"/>
            <XAxis
              dataKey="name"
              height={80}
              tick={<CustomXAxisTick />}
              tickLine={false}
              axisLine={false}
              interval={0}
            />
            <YAxis
              yAxisId="value"
              width={80}
              tickFormatter={formatCurrency}
              //domain={calculateYDomain()}
              domain={[0, 100]}
              //ticks={generateYAxisTicks(calculateYDomain()[1])}
              ticks={[0, 20, 40, 60, 80, 100]}
              interval={0} // Force display all ticks
            />
            <YAxis
              yAxisId="percentage"
              orientation="right"
              width={50}
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
              ticks={[0, 20, 40, 60, 80, 100]}
            />

              {/* Health percentage bars */}
              <Bar
              yAxisId="percentage"
              dataKey="healthPercentage"
              fill="#FCD842"
              name="Health Percentage"
              opacity={0.7}
              radius={[4, 4, 0, 0]}
            />

            {/* Main value lines */}
            <Line
              yAxisId="value"
              type="monotone"
              dataKey="currentValue"
              stroke="#2563eb"
              strokeWidth={2}
              dot={false}
              name="Current Value (CV)"
            />

            <Line
              yAxisId="value"
              type="monotone"
              dataKey="baselineValue"
              stroke="#16a34a"
              strokeWidth={2}
              dot={false}
              name="Baseline Value (VB)"
            />

            <Tooltip content={<CustomTooltip />} />

          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Helper function to generate appropriate Y-axis ticks
function generateYAxisTicks(maxValue: number): number[] {
  if (maxValue <= 0) {
    return [0, 200000, 400000, 600000, 800000, 1000000];
  }

  // For values around $1M, create specific intervals
  if (maxValue >= 800000 && maxValue <= 1200000) {
    return [0, 200000, 400000, 600000, 800000, 1000000, 1200000];
  }

  // For values between $1M and $2M
  if (maxValue > 1000000 && maxValue <= 2000000) {
    return [0, 250000, 500000, 750000, 1000000, 1250000, 1500000, 1750000, 2000000]
      .filter(tick => tick <= maxValue);
  }

  // For values between $500k and $1M
  if (maxValue > 500000 && maxValue < 1000000) {
    return [0, 100000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 900000, 1000000]
      .filter(tick => tick <= maxValue);
  }

  // For other ranges, calculate a nice interval
  const tickCount = 8; // Increase number of ticks for better granularity
  let interval = maxValue / tickCount;

  // Round interval to a nice number
  const magnitude = Math.pow(10, Math.floor(Math.log10(interval)));
  const residual = interval / magnitude;

  if (residual > 5) {
    interval = 10 * magnitude;
  } else if (residual > 2) {
    interval = 5 * magnitude;
  } else if (residual > 1) {
    interval = 2 * magnitude;
  } else {
    interval = magnitude;
  }

  const ticks: number[] = [0];
  let currentTick = interval;

  while (currentTick <= maxValue * 1.1) { // Add 10% headroom
    ticks.push(currentTick);
    currentTick += interval;
    if (ticks.length > 12) break; // Limit maximum number of ticks
  }

  return ticks;
} 